import React, { useCallback } from "react";

import { faEllipsisH } from "@fortawesome/pro-solid-svg-icons/faEllipsisH";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { MenuItemType, SubMenuType } from "antd/es/menu/hooks/useItems";
import cn from "classnames";
import { MenuInfo } from "rc-menu/lib/interface";

import { Dropdown } from "@mapmycustomers/ui";

import ListWithOverflowItem from "@app/component/ListWithOverflowItem";
import RoundButton from "@app/component/RoundButton";

import ActionButton from "./ActionButton";
import styles from "./ActionsBar.module.scss";
import SubMenuActionButton from "./SubMenuActionButton";

export type ActionBarItemType = MenuItemType | SubMenuType;

interface Props {
  actions: ActionBarItemType[];
  className?: string;
  onClick?: (info: MenuInfo) => void;
}

const ActionsBar = ({ actions, className, onClick }: Props) => {
  const getParentElement = useCallback((trigger: HTMLElement) => trigger.parentElement!, []);
  const overflowRenderer = useCallback(
    (hiddenItemsCount: number) => (
      <Dropdown
        className={styles.menu}
        getPopupContainer={getParentElement}
        menu={{
          getPopupContainer: getParentElement,
          items: actions.slice(-hiddenItemsCount).map(({ icon, ...restProps }) => ({
            className: cn(styles.menuItem, `mmc-js-data-item-id--${restProps.key}`),
            "data-item-id": restProps.key,
            ...restProps,
          })),
          onClick,
          triggerSubMenuAction: "click",
        }}
        placement="bottomRight"
        trigger={["click"]}
      >
        <RoundButton
          className={styles.dropdownButton}
          data-item-id="ellipsis"
          leftIcon={<FontAwesomeIcon icon={faEllipsisH} />}
        />
      </Dropdown>
    ),
    [actions, getParentElement, onClick]
  );

  return (
    <div className={cn(styles.container, className)}>
      <ListWithOverflowItem className={styles.list} overflowRender={overflowRenderer}>
        {actions.map((action: ActionBarItemType) =>
          "children" in action && action.children ? (
            <SubMenuActionButton action={action} key={action.key} />
          ) : (
            <ActionButton
              className={styles.roundButton}
              data-item-id={action.key}
              disabled={action.disabled}
              key={action.key}
              leftIcon={action.icon}
              onClick={(e) =>
                action.onClick?.({
                  domEvent: e,
                  key: String(action.key),
                  keyPath: [String(action.key)],
                } as MenuInfo)
              }
            >
              {action?.label}
            </ActionButton>
          )
        )}
      </ListWithOverflowItem>
    </div>
  );
};

export default ActionsBar;
